import { Controller } from "@hotwired/stimulus"
import { loadMapLibre, loadBaseStyle } from "../lib/maplibre"

// A walk on the map: walking legs as a solid line along the streets, Tube or cab hops dashed, and a
// numbered pin for each stop. The route comes from Walk#route_data.
//
// With card, it's the still map on a walk's preview card (walk_cards#show): no controls, bolder lines
// and pins, and the page is marked ready (html[data-card-ready]) once the map and fonts have drawn, so
// WalkPreviewCamera knows when to take its screenshot.
//
// With still, it's a small map that only shows the route (a walk's card in a guide): no controls or
// panning, and it only loads once it's on screen, since it may start out in a hidden tab.
const FIT_PADDING = { top: 48, bottom: 40, left: 40, right: 56 }
const CARD_PADDING = 56
const MAX_FIT_ZOOM = 16
const STILL_PADDING = 40

export default class extends Controller {
  static targets = ["container"]
  static values = { route: Object, card: Boolean, still: Boolean }

  connect() {
    this.generation = (this.generation || 0) + 1
    // Turbo's cached page snapshots can include the previous map
    this.containerTarget.replaceChildren()
    if (!this.stillValue) return this.load(this.generation)

    const generation = this.generation
    this.observer = new IntersectionObserver(entries => {
      if (entries.some(entry => entry.isIntersecting)) {
        this.observer.disconnect()
        this.load(generation)
      }
    }, { rootMargin: "200px 0px" })
    this.observer.observe(this.element)
  }

  disconnect() {
    this.observer?.disconnect()
    this.generation++
    this.map?.remove()
    this.map = null
  }

  async load(generation) {
    const { pins = [], legs = [] } = this.routeValue
    if (pins.length === 0) return this.hide()

    let maplibregl, style
    try {
      [maplibregl, style] = await Promise.all([loadMapLibre(), loadBaseStyle()])
    } catch (error) {
      console.warn("Map unavailable:", error)
      return this.hide()
    }
    if (generation !== this.generation) return

    const still = this.cardValue || this.stillValue
    const options = still
      ? { interactive: false, attributionControl: false, fadeDuration: 0, fitBoundsOptions: { padding: this.cardValue ? CARD_PADDING : STILL_PADDING, maxZoom: MAX_FIT_ZOOM } }
      : {
          fitBoundsOptions: { padding: FIT_PADDING, maxZoom: MAX_FIT_ZOOM },
          attributionControl: { compact: true },
          // One finger scrolls the page on phones; two fingers (or ctrl + scroll) move the map
          cooperativeGestures: true,
          dragRotate: false,
          pitchWithRotate: false,
          touchPitch: false
        }
    try {
      this.map = new maplibregl.Map({ container: this.containerTarget, style, bounds: routeBounds(pins, legs), ...options })
    } catch (error) {
      // e.g. WebGL is unavailable on this device
      console.warn("Map unavailable:", error)
      return this.hide()
    }

    if (!still) {
      this.map.touchZoomRotate.disableRotation()
      this.map.addControl(new maplibregl.NavigationControl({ showCompass: false }), "top-right")
    }
    this.map.on("load", () => {
      this.drawRoute(legs)
      if (this.cardValue) this.map.once("idle", () => this.ready("map"))
    })

    pins.forEach((pin) => {
      const element = document.createElement("div")
      element.className = this.cardValue ? "walk-pin walk-pin--card" : "walk-pin"
      element.textContent = pin.label
      element.setAttribute("role", "img")
      element.setAttribute("aria-label", `Stop ${pin.label}: ${pin.title}`)
      new maplibregl.Marker({ element }).setLngLat([pin.lng, pin.lat]).addTo(this.map)
    })
  }

  // Tells the screenshot it can be taken: "map" once everything has drawn, "failed" when there's no map
  async ready(state) {
    if (!this.cardValue) return

    await document.fonts.ready
    document.documentElement.dataset.cardReady = state
  }

  drawRoute(legs) {
    const card = this.cardValue
    const lines = (mode) => ({
      type: "FeatureCollection",
      features: legs.filter(leg => leg.mode === mode && leg.coordinates.length > 1).map(leg => ({
        type: "Feature",
        properties: {},
        geometry: { type: "LineString", coordinates: leg.coordinates }
      }))
    })

    this.map.addSource("walk-route", { type: "geojson", data: lines("walk") })
    this.map.addSource("walk-transit", { type: "geojson", data: lines("transit") })

    this.map.addLayer({
      id: "walk-transit", type: "line", source: "walk-transit",
      layout: { "line-cap": "round" },
      paint: { "line-color": "#1c1917", "line-width": card ? 4 : 2.5, "line-dasharray": [0.2, 2.5] }
    })
    this.map.addLayer({
      id: "walk-route-casing", type: "line", source: "walk-route",
      layout: { "line-cap": "round", "line-join": "round" },
      paint: { "line-color": "#ffffff", "line-width": card ? 12 : 8 }
    })
    this.map.addLayer({
      id: "walk-route", type: "line", source: "walk-route",
      layout: { "line-cap": "round", "line-join": "round" },
      paint: { "line-color": "#1c1917", "line-width": card ? 6 : 4 }
    })
  }

  hide() {
    this.element.hidden = true
    this.ready("failed")
  }
}

function routeBounds(pins, legs) {
  const points = pins.map(pin => [pin.lng, pin.lat])
  legs.filter(leg => leg.mode === "walk").forEach(leg => points.push(...leg.coordinates))

  const lngs = points.map(([lng]) => lng)
  const lats = points.map(([, lat]) => lat)
  return [[Math.min(...lngs), Math.min(...lats)], [Math.max(...lngs), Math.max(...lats)]]
}
